// Port of webapp/src/components/viewHeader/viewHeader.tsx and the three menus
// next to it (viewHeaderGroupByMenu / viewHeaderSortMenu /
// viewHeaderPropertiesMenu / viewHeaderDisplayByMenu / viewHeaderActionsMenu),
// grouped here because they share the same small trigger+menu shape.

import {ReactElement, useMemo} from 'react'
import classnames from 'classnames'
import {useNavigate} from 'react-router-dom'
import {Board} from '../../types/board'
import {BoardView, IViewType, viewTypes} from '../../types/boardView'
import {Card} from '../../types/card'
import {IUser} from '../../types/user'
import {Constants} from '../../common/constants'
import {buildBoardPath} from '../../common/paths'
import * as mutator from '../../state/mutator'
import {Icon, IconName, viewIcons} from '../common/icons'
import {ButtonWithMenu, Menu, MenuItem, MenuSeparator} from '../common/menu'
import FilterComponent from './filterComponent'

type Props = {
    board: Board
    views: BoardView[]
    view: BoardView
    cards: Card[]
    visibleCardCount: number
    usersById: Record<string, IUser>
    teamId: string
    readOnly: boolean
    onCardClicked: (cardId: string) => void
}

export default function ViewHeader(props: Props): ReactElement {
    const {board, views, view, cards, visibleCardCount, usersById, teamId, readOnly, onCardClicked} = props
    const navigate = useNavigate()

    const groupable = useMemo(() => board.cardProperties.filter((p) => ['select', 'multiSelect', 'person', 'multiPerson', 'createdBy', 'updatedBy'].includes(p.type)), [board.cardProperties])
    const dateProps = useMemo(() => board.cardProperties.filter((p) => p.type === 'date'), [board.cardProperties])

    const goView = (viewId: string): void => navigate(buildBoardPath({teamId, boardId: board.id, viewId}))

    const addCard = async (): Promise<void> => {
        const card = await mutator.createCard(board, view, {})
        onCardClicked(card.id)
    }

    const shownPropertyIds = (): string[] =>
        view.fields.visiblePropertyIds.length ? view.fields.visiblePropertyIds : board.cardProperties.map((p) => p.id)

    const addView = async (viewType: IViewType): Promise<void> => {
        const created = await mutator.createView(board, viewType)
        goView(created.id)
    }

    const duplicateView = async (): Promise<void> => {
        const created = await mutator.createView(board, view.fields.viewType)
        await mutator.patchViewFields(board.id, created, {...view.fields})
        await mutator.renameView(board.id, created, `${view.title}（副本）`)
        goView(created.id)
    }

    return (
        <div className='ViewHeader flex flex-wrap items-center gap-x-4 gap-y-2 pb-2 pt-1'>
            {/* view tabs */}
            <nav className='flex items-center gap-1' aria-label='视图'>
                {views.map((item) => (
                    <button
                        key={item.id}
                        type='button'
                        onClick={() => goView(item.id)}
                        className={classnames(
                            'flex items-center gap-1.5 rounded px-2 py-1 text-sm',
                            item.id === view.id ? 'bg-main-fg/10 font-semibold' : 'opacity-70 hover:bg-main-fg/5',
                        )}
                        aria-current={item.id === view.id ? 'page' : undefined}
                    >
                        <Icon name={viewIcons[item.fields.viewType]} size={14}/>
                        {item.title}
                    </button>
                ))}
                {!readOnly && (
                    <ButtonWithMenu
                        button={
                            <span className='flex cursor-pointer items-center gap-1 rounded px-2 py-1 text-sm opacity-70 hover:bg-main-fg/10'>
                                <Icon name='add' size={14}/>
                            </span>
                        }
                    >
                        <Menu ariaLabel='添加视图'>
                            {viewTypes.map((item) => (
                                <MenuItem
                                    key={item.type}
                                    icon={viewIcons[item.type]}
                                    onClick={() => void addView(item.type)}
                                >
                                    {item.label}
                                </MenuItem>
                            ))}
                        </Menu>
                    </ButtonWithMenu>
                )}
            </nav>

            <div className='ml-auto flex flex-wrap items-center gap-1'>
                {view.fields.viewType === 'board' && !readOnly && (
                    <ButtonWithMenu
                        button={<Trigger icon='view-board' label='分组方式'/>}
                    >
                        <Menu ariaLabel='分组依据'>
                            {groupable.map((p) => (
                                <MenuItem
                                    key={p.id}
                                    checked={view.fields.groupById === p.id}
                                    onClick={() => mutator.changeViewGroupBy(board.id, view, p.id)}
                                >
                                    {p.name}
                                </MenuItem>
                            ))}
                            <MenuSeparator/>
                            <MenuItem checked={!view.fields.groupById} onClick={() => mutator.changeViewGroupBy(board.id, view, undefined)}>不分组</MenuItem>
                        </Menu>
                    </ButtonWithMenu>
                )}

                {view.fields.viewType === 'calendar' && (
                    <ButtonWithMenu button={<Trigger icon='date' label='日历展示依据'/>}>
                        <Menu ariaLabel='日历日期属性'>
                            {dateProps.map((p) => (
                                <MenuItem
                                    key={p.id}
                                    checked={view.fields.dateDisplayPropertyId === p.id}
                                    onClick={() => mutator.changeViewShowBy(board.id, view, p.id)}
                                >
                                    {p.name}
                                </MenuItem>
                            ))}
                            {dateProps.length === 0 && <MenuItem disabled={true}>该看板还没有日期属性</MenuItem>}
                        </Menu>
                    </ButtonWithMenu>
                )}

                <ButtonWithMenu button={<Trigger icon='sort-asc' label='排序'/>}>
                    <Menu ariaLabel='排序菜单'>
                        <MenuItem
                            checked={view.fields.sortOptions.length === 0}
                            onClick={() => mutator.changeViewSortBy(board.id, view, '', false)}
                        >
                            手动排序
                        </MenuItem>
                        <MenuSeparator/>
                        <div className='px-3 py-1 text-xxs uppercase opacity-60'>升序排列</div>
                        <MenuItem
                            icon='title'
                            checked={view.fields.sortOptions.some((o) => o.propertyId === Constants.titleColumnId && !o.reversed)}
                            onClick={() => mutator.changeViewSortBy(board.id, view, Constants.titleColumnId, false)}
                        >
                            标题
                        </MenuItem>
                        {board.cardProperties.map((p) => (
                            <MenuItem
                                key={`asc-${p.id}`}
                                checked={view.fields.sortOptions.some((o) => o.propertyId === p.id && !o.reversed)}
                                onClick={() => mutator.changeViewSortBy(board.id, view, p.id, false)}
                            >
                                {p.name}
                            </MenuItem>
                        ))}
                        <MenuSeparator/>
                        <div className='px-3 py-1 text-xxs uppercase opacity-60'>降序排列</div>
                        <MenuItem
                            icon='title'
                            checked={view.fields.sortOptions.some((o) => o.propertyId === Constants.titleColumnId && o.reversed)}
                            onClick={() => mutator.changeViewSortBy(board.id, view, Constants.titleColumnId, true)}
                        >
                            标题
                        </MenuItem>
                        {board.cardProperties.map((p) => (
                            <MenuItem
                                key={`desc-${p.id}`}
                                checked={view.fields.sortOptions.some((o) => o.propertyId === p.id && o.reversed)}
                                onClick={() => mutator.changeViewSortBy(board.id, view, p.id, true)}
                            >
                                {p.name}
                            </MenuItem>
                        ))}
                    </Menu>
                </ButtonWithMenu>

                <FilterComponent board={board} view={view} usersById={usersById} readOnly={readOnly}/>

                {!readOnly && (
                    <ButtonWithMenu button={<Trigger icon='add' label='新建'/>}>
                        <Menu ariaLabel='新建卡片'>
                            <MenuItem icon='add' onClick={() => void addCard()}>新建</MenuItem>
                            <MenuSeparator/>
                            <MenuItem icon='duplicate' onClick={() => void duplicateView()}>创建视图副本</MenuItem>
                            <MenuItem
                                icon='trash'
                                danger={true}
                                disabled={views.length < 2}
                                onClick={() => void mutator.archiveView(board.id, view)}
                            >
                                删除视图
                            </MenuItem>
                        </Menu>
                    </ButtonWithMenu>
                )}

                <ButtonWithMenu button={<Trigger icon='eye' label='属性'/>}>
                    <Menu ariaLabel='显示属性'>
                        {board.cardProperties.length === 0 && <MenuItem disabled={true}>暂无属性</MenuItem>}
                        {board.cardProperties.map((p) => (
                            <MenuItem
                                key={p.id}
                                checked={shownPropertyIds().includes(p.id)}
                                onClick={() => {
                                    const shown = shownPropertyIds()
                                    const next = shown.includes(p.id) ? shown.filter((id) => id !== p.id) : [...shown, p.id]
                                    void mutator.changeViewVisibleProperties(board.id, view, next)
                                }}
                            >
                                {p.name}
                            </MenuItem>
                        ))}
                    </Menu>
                </ButtonWithMenu>

                <span className='px-2 text-xs opacity-60' aria-live='polite'>
                    {visibleCardCount === cards.length ? `${cards.length} 张卡片` : `${cards.length} 张中的 ${visibleCardCount} 张`}
                </span>
            </div>

        </div>
    )
}

function Trigger({icon, label}: {icon: IconName, label: string}): ReactElement {
    return (
        <span className='flex cursor-pointer items-center gap-1 rounded px-2 py-1 text-sm hover:bg-main-fg/10'>
            <Icon name={icon} size={14}/>
            <span className='hidden md:inline'>{label}</span>
            <Icon name='chevron-down' size={12} className='opacity-60'/>
        </span>
    )
}
